/**
 * Route Preloader Utility
 * Preloads critical routes to improve navigation performance
 */

type PreloadableRoute = () => Promise<{ default: React.ComponentType }>;

const preloadedRoutes = new Set<string>();

/**
 * Preload a route component
 */
export const preloadRoute = (routeName: string, loader: PreloadableRoute): void => {
  if (preloadedRoutes.has(routeName)) {
    return;
  }

  preloadedRoutes.add(routeName);
  
  // Use requestIdleCallback for non-critical preloading
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      loader().catch(() => {
        // Remove from set if preload fails
        preloadedRoutes.delete(routeName);
      });
    }, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      loader().catch(() => {
        preloadedRoutes.delete(routeName);
      });
    }, 1);
  }
};

/**
 * Preload multiple routes
 */
export const preloadRoutes = (routes: Array<{ name: string; loader: PreloadableRoute }>): void => {
  routes.forEach(({ name, loader }) => {
    preloadRoute(name, loader);
  });
};

/**
 * Check if a route has been preloaded
 */
export const isRoutePreloaded = (routeName: string): boolean => {
  return preloadedRoutes.has(routeName);
};

/**
 * Clear preloaded routes cache
 */
export const clearPreloadCache = (): void => {
  preloadedRoutes.clear();
};
